import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaProjectDiagram, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaUsers, 
  FaMoneyBillWave,
  FaEye,
  FaEdit,
  FaPlus
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const AddButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(20, 19, 64, 0.1);
  }
`;

const ProjectHeader = styled.div<{ status: string }>`
  background: ${props => {
    switch (props.status) {
      case 'In Progress': return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      case 'Completed': return 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)';
      case 'Draft': return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
      default: return 'linear-gradient(135deg, #141340 0%, #2a2868 100%)';
    }
  }};
  padding: 1.5rem;
  color: white;
  position: relative;
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }
  
  .project-id {
    font-size: 0.85rem;
    opacity: 0.9;
  }
  
  .status-badge {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const ProjectBody = styled.div`
  padding: 1.5rem;
`;

const ProjectInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .info-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #6c757d;
    
    .label {
      color: #141340;
      font-weight: 500;
    }
    
    .icon {
      color: #F68B21;
      font-size: 0.9rem;
    }
  }
`;

const ProjectProgress = styled.div`
  margin-bottom: 1.5rem;
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    
    .label {
      font-weight: 500;
      color: #141340;
    }
    
    .value {
      color: #F68B21;
      font-weight: 600;
    }
  }
  
  .progress-bar {
    height: 8px;
    background: #f1f1f1;
    border-radius: 4px;
    overflow: hidden;
    
    .fill {
      height: 100%;
      background: linear-gradient(90deg, #F68B21 0%, #e67c1a 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 1rem;
  
  a {
    flex: 1;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #f5f7fa;
    color: #141340;
    border-radius: 8px;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(246, 139, 33, 0.1);
      color: #F68B21;
    }
    
    .icon {
      font-size: 0.9rem;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  
  .icon {
    font-size: 4rem;
    color: #dee2e6;
    margin-bottom: 1rem;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: #495057;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
`;

const ManagerProjectsList: React.FC = () => {
  const { user } = useAuth();
  const [assignedProjects, setAssignedProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchManagerProjects = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Get assigned projects for the current manager (backend now handles filtering)
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const managerProjects = assignedProjectsResponse.data || [];
        
        console.log('Manager projects:', managerProjects);
        setAssignedProjects(managerProjects);
        
      } catch (error) {
        console.error('Error fetching manager projects:', error);
        setAssignedProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchManagerProjects();
  }, [user]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  if (assignedProjects.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>
            <IconWrapper icon={FaProjectDiagram} className="icon" />
            My Projects
          </h1>
        </PageHeader>
        
        <EmptyState>
          <IconWrapper icon={FaProjectDiagram} className="icon" />
          <h3>No Projects Assigned</h3>
          <p>You don't have any projects assigned to you yet.</p>
        </EmptyState>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHeader>
        <h1>
          <IconWrapper icon={FaProjectDiagram} className="icon" />
          My Projects
        </h1>
        <AddButton to="/manager/projects/new">
          <IconWrapper icon={FaPlus} />
          Add Project
        </AddButton>
      </PageHeader>
      
      <ProjectsGrid>
        {assignedProjects.map(project => (
          <ProjectCard key={project.id || project._id}>
            <ProjectHeader status={project.status}>
              <h3>{project.name}</h3>
              <div className="project-id">{project.id || project._id}</div>
              <div className="status-badge">{project.status}</div>
            </ProjectHeader>
            <ProjectBody>
              <ProjectInfo>
                <div className="info-item">
                  <IconWrapper icon={FaMapMarkerAlt} className="icon" />
                  <span className="label">Location:</span> {project.location}
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaCalendarAlt} className="icon" />
                  <span className="label">Deadline:</span> {formatDate(project.endDate || new Date().toISOString())}
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaUsers} className="icon" />
                  <span className="label">Team:</span> {project.teamSize || 0} members
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaMoneyBillWave} className="icon" />
                  <span className="label">Budget:</span> {formatCurrency(project.budget.total)}
                </div>
              </ProjectInfo>
              
              <ProjectProgress>
                <div className="progress-header">
                  <div className="label">Progress</div>
                  <div className="value">{project.progress || 0}%</div>
                </div>
                <div className="progress-bar">
                  <div className="fill" style={{ width: `${project.progress || 0}%` }}></div>
                </div>
              </ProjectProgress>
              
              <ProjectActions>
                <Link to={`/manager/projects/${project.id || project._id}`}>
                  <IconWrapper icon={FaEye} className="icon" />
                  View Details
                </Link>
                <Link to={`/manager/projects/${project.id || project._id}/edit`}>
                  <IconWrapper icon={FaEdit} className="icon" />
                  Manage
                </Link>
              </ProjectActions>
            </ProjectBody>
          </ProjectCard>
        ))}
      </ProjectsGrid>
    </PageContainer>
  );
};

export default ManagerProjectsList; 